import React, { useState } from 'react';
import { Send, Undo2, Check, X, AlertCircle, History, ClipboardCheck, Globe, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import ReviewHistory from './ReviewHistory';
import PublishAssessmentModal from './PublishAssessmentModal';
import {
  submitAssessmentForReview,
  withdrawAssessment,
  approveAssessment,
  denyAssessment,
  publishAssessment,
  submitStudyDraftForApproval,
  withdrawStudyDraft,
  approveStudyDraft,
  denyStudyDraft,
  publishStudyDraft,
  revokeStudyDraftApproval,
  recallStudyDraft,
  getReviewHistory,
} from '../../services/reviewWorkflowApi';

/**
 * Review-workflow action buttons for a single entity (assessment or study draft).
 *
 * Renders only the actions that are legal from the entity's current review state
 * and the viewer's role:
 *   - draft / denied        -> Submit for review
 *   - in_review (submitter) -> Withdraw
 *   - in_review (approver)  -> Approve, Deny (with required reason)
 *
 * The backend transition service is the source of truth and re-validates every
 * action, so a stale button just yields a 4xx the user sees as a toast.
 *
 * Props:
 *   entity        - the assessment/draft object (needs uuid|id + status fields)
 *   entityType    - 'assessment' | 'study'   (default 'assessment')
 *   currentUserId - id of the logged-in user (to detect the submitter)
 *   isApprover    - whether to show the Review action
 *   onChanged     - called after any successful transition (refresh callback)
 *   onReview      - called with the entity when the approver clicks "Review"
 *                   (opens the content + bottom Approve/Deny bar). When omitted,
 *                   approve/deny fall back to inline buttons.
 *   stopPropagation - stop click bubbling (for use inside a clickable card)
 */
const REVIEW_API = {
  assessment: {
    submit: submitAssessmentForReview,
    withdraw: withdrawAssessment,
    approve: approveAssessment,
    deny: denyAssessment,
    publish: publishAssessment,
  },
  study: {
    submit: submitStudyDraftForApproval,
    withdraw: withdrawStudyDraft,
    approve: approveStudyDraft,
    deny: denyStudyDraft,
    publish: publishStudyDraft,
    revoke: revokeStudyDraftApproval,
    recall: recallStudyDraft,
  },
};

const reviewStatus = (entity) =>
  entity?.review_status || entity?.publication_status || entity?.status || 'draft';

const submitterId = (entity) => {
  const s = entity?.submitted_by;
  return s && typeof s === 'object' ? s.id : s;
};

const submitterEmail = (entity) => {
  const s = entity?.submitted_by;
  if (s && typeof s === 'object' && s.email) return s.email;
  return entity?.submitted_by_email || null;
};

// The auth payload exposes the user's email (not their pk), so email is the
// reliable identity for "is the current user the submitter?".
const currentUserEmail = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}')?.email ?? null;
  } catch {
    return null;
  }
};

const errorMessage = (err, fallback) => {
  const data = err?.response?.data;
  // Publish surfaces a list of blocking config errors; join them onto the detail
  // so the user sees exactly what to fix (e.g. unnamed timepoints).
  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${data.detail || fallback} ${data.errors.join(' ')}`;
  }
  return data?.detail || data?.reason || fallback;
};

const ReviewWorkflowActions = ({
  entity,
  entityType = 'assessment',
  currentUserId,
  isApprover = false,
  onChanged,
  onReview,
  stopPropagation = false,
  size = 'sm',
}) => {
  const [busy, setBusy] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitNote, setSubmitNote] = useState('');
  // Most recent denial reason, shown in the submit dialog when resubmitting a
  // previously-denied item so the author can confirm it's addressed (FECOA-1709).
  const [denialReason, setDenialReason] = useState('');
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  // Shared confirm dialog for the reversal actions (revoke / recall, FECOA-1674).
  const [confirmAction, setConfirmAction] = useState(null);

  const api = REVIEW_API[entityType] || REVIEW_API.assessment;
  const ident = entityType === 'assessment' ? entity?.uuid : entity?.id;
  const status = reviewStatus(entity);
  const myEmail = currentUserEmail();
  const byId =
    currentUserId != null &&
    submitterId(entity) != null &&
    String(currentUserId) === String(submitterId(entity));
  const byEmail =
    !!myEmail &&
    !!submitterEmail(entity) &&
    myEmail.toLowerCase() === String(submitterEmail(entity)).toLowerCase();
  const isSubmitter = byId || byEmail;
  // Fail-safe for the approver actions: only offer Review / Approve / Deny when
  // we can POSITIVELY place the viewer as someone other than the submitter. That
  // requires the entity to actually tell us who submitted it. A partial or
  // mid-refetch payload (the list silently refetches on a timer) can arrive
  // without submitted_by / submitted_by_email, which would make isSubmitter read
  // false and briefly flip a submitter's "Withdraw" into a "Review" link. When
  // the submitter is unknown we withhold the approver actions instead — the
  // backend already blocks self-approval, this keeps the UI from ever offering
  // it. Steady-state payloads always carry these fields, so this only suppresses
  // during the transient window.
  const submitterKnown = submitterId(entity) != null || !!submitterEmail(entity);
  const confirmedNotSubmitter = submitterKnown && !isSubmitter;

  const guard = (e) => {
    if (stopPropagation && e) e.stopPropagation();
  };

  const run = async (fn, successMsg, fallbackErr) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      onChanged && onChanged();
    } catch (err) {
      toast.error(errorMessage(err, fallbackErr));
    } finally {
      setBusy(false);
    }
  };

  const openSubmit = (e) => {
    guard(e);
    setSubmitNote('');
    setDenialReason('');
    setSubmitOpen(true);
    // Surface the most recent denial reason so the author can confirm it's been
    // addressed before resubmitting (FECOA-1709). A denial counts as unresolved
    // whether the item is still `denied` or was withdrawn back to `draft`, so we
    // read the history rather than the current status — but hide it once a later
    // approval/publish has cleared that denial. Non-fatal: the banner just stays
    // hidden if history can't load.
    if (entity?.id == null) return;
    getReviewHistory(entityType, entity.id)
      .then((data) => {
        const rows = Array.isArray(data) ? data : data?.results || [];
        const when = (r) => new Date(r.decided_at || r.submitted_at).getTime();
        const latestDenial = rows
          .filter((r) => r.status === 'denied' && r.decision_reason)
          .sort((a, b) => when(b) - when(a))[0];
        if (!latestDenial) return;
        const resolvedByLaterApproval = rows.some(
          (r) => (r.status === 'approved' || r.status === 'published') && when(r) > when(latestDenial),
        );
        if (!resolvedByLaterApproval) setDenialReason(latestDenial.decision_reason);
      })
      .catch(() => {});
  };

  const confirmSubmit = async (e) => {
    guard(e);
    await run(
      () => api.submit(ident, submitNote.trim()),
      'Submitted for review.',
      'Could not submit for review.',
    );
    setSubmitOpen(false);
  };

  const handleWithdraw = (e) => {
    guard(e);
    run(() => api.withdraw(ident), 'Submission withdrawn.', 'Could not withdraw.');
  };

  const handleApprove = (e) => {
    guard(e);
    run(() => api.approve(ident), 'Approved.', 'Could not approve.');
  };

  const openDeny = (e) => {
    guard(e);
    setDenyReason('');
    setDenyOpen(true);
  };

  const confirmDeny = async (e) => {
    guard(e);
    if (!denyReason.trim()) return;
    await run(() => api.deny(ident, denyReason.trim()), 'Changes requested.', 'Could not deny.');
    setDenyOpen(false);
  };

  const handlePublish = (e) => {
    guard(e);
    // Assessments capture a change summary + optional version bump in a modal;
    // study drafts publish directly (their design was already locked at approval).
    if (entityType === 'assessment') {
      setPublishOpen(true);
      return;
    }
    run(() => api.publish(ident), 'Study published.', 'Could not publish study.');
  };

  const openRevoke = (e) => {
    guard(e);
    setConfirmAction({
      title: 'Revoke Approval?',
      message: `Are you sure you want to revoke the approval for ${entity?.name || 'this study'}? `
        + 'This will revert the study to a draft and require a new review cycle before it can be published.',
      confirmLabel: 'Revoke Approval',
      fn: () => api.revoke(ident),
      success: 'Approval revoked. Study is now a draft.',
      fallback: 'Could not revoke approval.',
    });
  };

  const openRecall = (e) => {
    guard(e);
    setConfirmAction({
      title: 'Recall Published Study?',
      message: `Recalling ${entity?.name || 'this study'} reverts the design to a draft so it can be edited. `
        + 'The live study stays in place; re-publish to apply any changes.',
      confirmLabel: 'Recall to Draft',
      fn: () => api.recall(ident),
      success: 'Study recalled. It is now a draft.',
      fallback: 'Could not recall study.',
    });
  };

  const confirmReversal = async (e) => {
    guard(e);
    const c = confirmAction;
    if (!c) return;
    await run(c.fn, c.success, c.fallback);
    setConfirmAction(null);
  };

  const btn = size === 'md' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs';
  // Self-publish: an assessment whose sponsor/study approval is not enforced skips
  // review entirely and can go live straight from draft (enforce_approval is
  // resolved server-side and shipped on the serialized assessment).
  const canSelfPublish =
    entityType === 'assessment' &&
    status === 'draft' &&
    entity?.enforce_approval === false &&
    typeof api.publish === 'function';
  const showSubmit = (status === 'draft' || status === 'denied') && !canSelfPublish;
  const showInReview = status === 'in_review' || status === 'review';
  const showPublish =
    (status === 'approved' || canSelfPublish) && typeof api.publish === 'function';
  // Reversal actions are approver-gated (backend requires approve permission).
  const showRevoke = status === 'approved' && isApprover && typeof api.revoke === 'function';
  const showRecall = status === 'published' && isApprover && typeof api.recall === 'function';
  const hasHistory = status !== 'draft' || submitterId(entity) != null;

  if (!ident) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5" onClick={(e) => stopPropagation && e.stopPropagation()}>
      {showSubmit && (
        <button
          onClick={openSubmit}
          disabled={busy}
          className={`${btn} font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50`}
          title="Submit for review"
        >
          <Send className="w-3.5 h-3.5" />
          <span>Submit for review</span>
        </button>
      )}

      {showInReview && isSubmitter && (
        <>
          <span className="text-xs text-blue-600 font-medium hidden sm:inline">Awaiting review</span>
          <button
            onClick={handleWithdraw}
            disabled={busy}
            className={`${btn} font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50`}
            title="Withdraw this submission so you can edit it again"
          >
            <Undo2 className="w-3.5 h-3.5" />
            <span>Withdraw</span>
          </button>
        </>
      )}

      {showInReview && isApprover && confirmedNotSubmitter && onReview && (
        <button
          onClick={(e) => {
            guard(e);
            onReview(entity);
          }}
          disabled={busy}
          className={`${btn} font-medium text-teal-700 hover:text-teal-800 hover:bg-teal-50 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50`}
          title="Open this submission to review and make a decision"
        >
          <ClipboardCheck className="w-3.5 h-3.5" />
          <span>Review</span>
        </button>
      )}

      {/* Fallback: inline approve/deny where no review screen is wired up */}
      {showInReview && isApprover && confirmedNotSubmitter && !onReview && (
        <>
          <button
            onClick={handleApprove}
            disabled={busy}
            className={`${btn} font-medium text-teal-700 hover:text-teal-800 hover:bg-teal-50 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50`}
            title="Approve"
          >
            <Check className="w-3.5 h-3.5" />
            <span>Approve</span>
          </button>
          <button
            onClick={openDeny}
            disabled={busy}
            className={`${btn} font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50`}
            title="Request changes"
          >
            <X className="w-3.5 h-3.5" />
            <span>Deny</span>
          </button>
        </>
      )}

      {showRevoke && (
        <button
          onClick={openRevoke}
          disabled={busy}
          className={`${btn} font-medium text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50`}
          title="Revoke approval and revert this study to a draft"
        >
          <Undo2 className="w-3.5 h-3.5" />
          <span>Revoke</span>
        </button>
      )}

      {showPublish && (
        <button
          onClick={handlePublish}
          disabled={busy}
          className={`${btn} font-medium text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50`}
          title="Publish this approved study to make it live"
        >
          <Globe className="w-3.5 h-3.5" />
          <span>Publish</span>
        </button>
      )}

      {showRecall && (
        <button
          onClick={openRecall}
          disabled={busy}
          className={`${btn} font-medium text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50`}
          title="Recall this published study back to an editable draft"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          <span>Recall</span>
        </button>
      )}

      {hasHistory && (
        <button
          onClick={(e) => {
            guard(e);
            setHistoryOpen(true);
          }}
          className={`${btn} font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors flex items-center gap-1`}
          title="Review history"
        >
          <History className="w-3.5 h-3.5" />
          <span>History</span>
        </button>
      )}

      {historyOpen && (
        <ReviewHistory
          entityType={entityType}
          objectId={entity.id}
          entityName={entity.name || entity.survey?.name}
          currentStatus={status}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {publishOpen && (
        <PublishAssessmentModal
          assessment={entity}
          onClose={() => setPublishOpen(false)}
          onPublished={() => { setPublishOpen(false); onChanged && onChanged(); }}
        />
      )}

      {/* Submit for review modal (FECOA-1590) */}
      {submitOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4 text-left whitespace-normal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSubmitOpen(false);
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 pt-6 pb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Send className="w-5 h-5 text-teal-600" />
                Submit for Review
              </h3>
              {(entity?.name || entity?.survey?.name) && (
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mt-2">
                  {entity.name || entity.survey?.name}
                </p>
              )}
              {denialReason && (
                <div className="mt-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 px-3 py-2">
                  <div className="flex items-center gap-1.5 text-red-700 dark:text-red-300 font-semibold text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    Previously denied — Reviewer feedback
                  </div>
                  <p className="mt-1 text-sm italic text-red-700/90 dark:text-red-200/90">“{denialReason}”</p>
                </div>
              )}
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2 text-sm text-blue-700 dark:text-blue-300">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Once submitted, this {entityType === 'study' ? 'study design' : 'assessment'} will be{' '}
                  <strong>locked from editing</strong> until an admin approves or denies the review.
                  You can withdraw the submission at any time to unlock it.
                </span>
              </div>
            </div>
            <div className="px-6 pb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
                Message to reviewer <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={submitNote}
                onChange={(e) => setSubmitNote(e.target.value)}
                rows={4}
                autoFocus
                placeholder="e.g. Completed all branching logic updates per protocol v3.2. Please focus review on screens 4-6."
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none text-sm"
              />
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => setSubmitOpen(false)}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmit}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              >
                <Send className="w-4 h-4" />
                {busy ? 'Submitting…' : 'Submit for Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deny reason modal */}
      {denyOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4 text-left whitespace-normal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDenyOpen(false);
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 pt-6 pb-4 flex items-start">
              <div className="flex-shrink-0 bg-red-100 rounded-full p-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Request changes</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  The submitter will be notified with your reason. This is required.
                </p>
              </div>
            </div>
            <div className="px-6 pb-2">
              <textarea
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                rows={4}
                autoFocus
                placeholder="Explain what needs to change before this can be approved…"
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
              />
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => setDenyOpen(false)}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeny}
                disabled={busy || !denyReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? 'Processing…' : 'Request changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reversal confirm modal — revoke approval / recall published (FECOA-1674) */}
      {confirmAction && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4 text-left whitespace-normal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmAction(null);
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 pt-6 pb-4 flex items-start">
              <div className="flex-shrink-0 bg-amber-100 rounded-full p-3">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{confirmAction.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{confirmAction.message}</p>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => setConfirmAction(null)}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmReversal}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? 'Processing…' : confirmAction.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewWorkflowActions;
